import {
  addDays,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import { formatDateShort } from "@entrilia/shared/utilities/dateUtils";
import { ReferenceDateType } from "../../../../../../api/types/dashboardsTypes";
import { parseIsoOrLocalFormat } from "@entrilia/shared/components/reporting/bc-datepicker/bcDatePickerHelper";
import { formatToISODate } from "../../../../reporting/utils/dateUtils";

export const getDateLabel = (type: ReferenceDateType, customValue: string[]): string => {
  const now = new Date();

  switch (type) {
    case ReferenceDateType.ThisMonth: {
      return format(now, "MMM yyyy");
    }
    case ReferenceDateType.ThisQuarter: {
      const quarter = getQuarter(now);
      return `Q${quarter} ${format(now, "yyyy")}`;
    }
    case ReferenceDateType.ThisYear: {
      return format(now, "yyyy");
    }
    case ReferenceDateType.LastMonth: {
      const lastMonth = subMonths(now, 1);
      return format(lastMonth, "MMM yyyy");
    }
    case ReferenceDateType.LastQuarter: {
      const lastQuarter = subQuarters(now, 1);
      const lastQuarterNumber = getQuarter(lastQuarter);
      return `Q${lastQuarterNumber} ${format(lastQuarter, "yyyy")}`;
    }
    case ReferenceDateType.LastYear: {
      const lastYear = subYears(now, 1);
      return format(lastYear, "yyyy");
    }
    case ReferenceDateType.Last7Days: {
      const sevenDaysAgo = subDays(now, 6);
      return `${format(sevenDaysAgo, "MMM dd")} - ${format(now, "MMM dd")}`;
    }
    case ReferenceDateType.Last30Days: {
      const thirtyDaysAgo = subDays(now, 29);
      return `${format(thirtyDaysAgo, "MMM dd")} - ${format(now, "MMM dd")}`;
    }
    case ReferenceDateType.Last12Months: {
      const twelveMonthsAgo = addDays(subMonths(now, 12), 1);
      return `${format(twelveMonthsAgo, "MMM dd, yyyy")} - ${format(now, "MMM dd, yyyy")}`;
    }
    case ReferenceDateType.CustomRange: {
      return customValue.map((value) => formatDateShort(parseIsoOrLocalFormat(value) ?? new Date())).join(" - ");
    }
    default: {
      return "";
    }
  }
};

export const getReferenceDateValues = (type: ReferenceDateType, customValue: string[]): string[] => {
  switch (type) {
    case ReferenceDateType.CustomRange: {
      return customValue;
    }
    case ReferenceDateType.ThisMonth: {
      return [formatToISODate(startOfMonth(new Date())), formatToISODate(endOfMonth(new Date()))];
    }
    case ReferenceDateType.ThisQuarter: {
      return [formatToISODate(startOfQuarter(new Date())), formatToISODate(endOfQuarter(new Date()))];
    }
    case ReferenceDateType.ThisYear: {
      return [formatToISODate(startOfYear(new Date())), formatToISODate(endOfYear(new Date()))];
    }
    case ReferenceDateType.LastMonth: {
      return [
        formatToISODate(startOfMonth(subMonths(new Date(), 1))),
        formatToISODate(endOfMonth(subMonths(new Date(), 1))),
      ];
    }
    case ReferenceDateType.LastQuarter: {
      return [
        formatToISODate(startOfQuarter(subQuarters(new Date(), 1))),
        formatToISODate(endOfQuarter(subQuarters(new Date(), 1))),
      ];
    }
    case ReferenceDateType.LastYear: {
      return [
        formatToISODate(startOfYear(subYears(new Date(), 1))),
        formatToISODate(endOfYear(subYears(new Date(), 1))),
      ];
    }
    case ReferenceDateType.Last7Days: {
      return [formatToISODate(subDays(new Date(), 6)), formatToISODate(new Date())];
    }
    case ReferenceDateType.Last30Days: {
      return [formatToISODate(subDays(new Date(), 29)), formatToISODate(new Date())];
    }
    case ReferenceDateType.Last12Months: {
      return [formatToISODate(addDays(subMonths(new Date(), 12), 1)), formatToISODate(new Date())];
    }
    default: {
      return [];
    }
  }
};
